import type { Process } from "../../api/types";
import { dayKeyForDate, localDayKey } from "../../utils";
import { fitsSimpleMap, selectCommonPaths } from "./processCommonPaths";
import { type MapSize, type ProcessInstance, SIMPLE_MAP_LIMIT } from "./processGraphModel";
import { caseDay } from "./processInstanceLabels";

/** Where a plain-language condition is in its round trip to the backend. */
export type ScopeQueryStatus = "idle" | "loading" | "error" | "no_matches";

/** A plain-language condition, already resolved to ids by the backend. */
export type InstanceScopeQuery = {
  text: string;
  instanceIds: string[];
  explanation: string | null;
};

export type InstanceScope = {
  apps?: string[];
  minDuration?: number;
  maxDuration?: number;
  minSteps?: number;
  maxSteps?: number;
  /**
   * Draw only the common complete paths within the other filters (true), or every case (false).
   * Unset leaves it to the population: simplified once drawing every case outgrows a simple map.
   */
  commonPaths?: boolean;
  /** An instance recorded by any of these users. Empty means "any user". */
  userIds: string[];
  /** An explicitly empty shared people selection, distinct from any user. */
  noUsers?: boolean;
  /** An instance visiting any of these nodes. Empty means "any node". */
  nodeIds: string[];
  /** Inclusive local-day bounds, `yyyy-mm-dd`. */
  from: string | null;
  to: string | null;
  query: InstanceScopeQuery | null;
  /** Ids unticked among the matches. Only ever narrows — it cannot pull in a non-match. */
  excludedIds: string[];
};

export const EMPTY_INSTANCE_SCOPE: InstanceScope = {
  userIds: [],
  nodeIds: [],
  from: null,
  to: null,
  query: null,
  excludedIds: [],
};

/** The supported structured and submitted filters. */
export type InstanceScopeCriterion =
  | "query"
  | "user"
  | "date"
  | "node"
  | "apps"
  | "duration"
  | "steps"
  | "commonPaths";

const INSTANCE_SCOPE_CRITERIA: InstanceScopeCriterion[] = [
  "query",
  "user",
  "date",
  "node",
  "apps",
  "duration",
  "steps",
  "commonPaths",
];

// ---------------------------------------------------------------------------
// Index

/**
 * Per-instance lookups the predicates need, derived once per process rather than per keystroke:
 * every criterion is re-evaluated on each edit of the draft scope to keep the live count honest.
 */
export type InstanceScopeIndex = {
  instances: ProcessInstance[];
  nodes: Process["nodes"];
  /** Process order, which is what every id list is normalized back to. */
  order: string[];
  dayById: Map<string, string>;
  /** The day an instance *ends* on, so a date range can be read as an overlap. */
  endDayById: Map<string, string>;
  nodeIdsById: Map<string, Set<string>>;
};

export const buildInstanceScopeIndex = (
  instances: ProcessInstance[],
  nodes: Process["nodes"] = [],
): InstanceScopeIndex => ({
  nodes,
  instances,
  order: instances.map(instance => instance.id),
  dayById: new Map(instances.map(instance => [instance.id, caseDay(instance.source)])),
  endDayById: new Map(
    instances.map(instance => [instance.id, localDayKey(instance.source.traceEnd)]),
  ),
  nodeIdsById: new Map(
    instances.map(instance => [instance.id, new Set(instance.steps.map(step => step.nodeId))]),
  ),
});

// ---------------------------------------------------------------------------
// Matching

type CriterionPredicate = (instance: ProcessInstance) => boolean;

const predicateFor = (
  criterion: InstanceScopeCriterion,
  scope: InstanceScope,
  index: InstanceScopeIndex,
): CriterionPredicate | null => {
  switch (criterion) {
    case "user": {
      if (scope.noUsers) return () => false;
      if (scope.userIds.length === 0) return null;
      const wanted = new Set(scope.userIds);
      return instance => wanted.has(instance.source.userId);
    }
    case "node": {
      if (scope.nodeIds.length === 0) return null;
      return instance => {
        const visited = index.nodeIdsById.get(instance.id);
        return !!visited && scope.nodeIds.some(nodeId => visited.has(nodeId));
      };
    }
    case "date": {
      if (!scope.from && !scope.to) return null;
      // Bounds are inclusive and read as an overlap: an instance that ran across midnight into
      // the window belongs to it as much as one that started inside it.
      return instance => {
        const start = index.dayById.get(instance.id);
        if (!start) return false;
        const end = index.endDayById.get(instance.id) ?? start;
        if (scope.from && end < scope.from) return false;
        if (scope.to && start > scope.to) return false;
        return true;
      };
    }
    case "apps":
      return scope.apps?.length
        ? instance =>
            instance.steps.some(step =>
              (scope.apps ?? []).some(app => (step.appSeconds?.[app] ?? 0) > 0),
            )
        : null;
    case "duration":
      return scope.minDuration !== undefined || scope.maxDuration !== undefined
        ? instance =>
            (scope.minDuration === undefined || instance.source.avgDuration >= scope.minDuration) &&
            (scope.maxDuration === undefined || instance.source.avgDuration <= scope.maxDuration)
        : null;
    case "steps":
      return scope.minSteps !== undefined || scope.maxSteps !== undefined
        ? instance =>
            (scope.minSteps === undefined || instance.steps.length >= scope.minSteps) &&
            (scope.maxSteps === undefined || instance.steps.length <= scope.maxSteps)
        : null;
    case "commonPaths":
      return null; // Evaluated after the independent predicates narrow the population.
    case "query": {
      if (!scope.query) return null;
      const matched = new Set(scope.query.instanceIds);
      return instance => matched.has(instance.id);
    }
  }
};

/** The criteria actually constraining anything, so empty ones never render a chip. */
export const activeScopeCriteria = (scope: InstanceScope): InstanceScopeCriterion[] =>
  INSTANCE_SCOPE_CRITERIA.filter(criterion => {
    switch (criterion) {
      case "user":
        return !!scope.noUsers || scope.userIds.length > 0;
      case "node":
        return scope.nodeIds.length > 0;
      case "date":
        return !!scope.from || !!scope.to;
      case "query":
        return !!scope.query;
      case "apps":
        return !!scope.apps?.length;
      case "steps":
        return scope.minSteps !== undefined || scope.maxSteps !== undefined;
      case "commonPaths":
        return !!scope.commonPaths;
      case "duration":
        return scope.minDuration !== undefined || scope.maxDuration !== undefined;
    }
  });

type InstanceScopeMatch = {
  /** Instances passing every filter, before the manual unticks. */
  matchedIds: string[];
  /** What the graph actually draws: matches minus the unticked ones. */
  includedIds: string[];
  /**
   * Only when nothing matches: the criteria that, dropped on their own, would bring matches
   * back. Names the condition responsible instead of an unhelpful blank list.
   */
  blockingCriteria: InstanceScopeCriterion[];
  /** Whether the graph draws common paths rather than every match, as chosen or by size. */
  simplified: boolean;
  commonPathsExample: boolean;
};

const idsMatching = (index: InstanceScopeIndex, predicates: CriterionPredicate[]): string[] =>
  index.instances
    .filter(instance => predicates.every(predicate => predicate(instance)))
    .map(instance => instance.id);

/** What a population of `ids` draws: every case, or its common paths when simplified. */
const drawnCases = (
  index: InstanceScopeIndex,
  ids: string[],
  commonPaths: boolean | undefined,
  limit: MapSize,
) => {
  const wanted = new Set(ids);
  const instances = index.instances.filter(instance => wanted.has(instance.id));
  const simplified = commonPaths ?? !fitsSimpleMap(instances, limit);
  const common = simplified ? selectCommonPaths(instances, limit) : null;
  return { simplified, ids: common?.ids ?? wanted, example: common?.isExample ?? false };
};

export const matchInstanceScope = (
  index: InstanceScopeIndex,
  scope: InstanceScope,
  limit: MapSize = SIMPLE_MAP_LIMIT,
): InstanceScopeMatch => {
  const active = activeScopeCriteria(scope);
  const predicates = new Map<InstanceScopeCriterion, CriterionPredicate>();
  for (const criterion of active) {
    const predicate = predicateFor(criterion, scope, index);
    if (predicate) predicates.set(criterion, predicate);
  }
  const candidates = idsMatching(index, [...predicates.values()]);
  const drawn = drawnCases(index, candidates, scope.commonPaths, limit);
  const matchedIds = drawn.simplified ? candidates.filter(id => drawn.ids.has(id)) : candidates;
  const excluded = new Set(scope.excludedIds);
  const includedIds = matchedIds.filter(id => !excluded.has(id));

  // Re-evaluate Common paths too: removing another filter changes its population.
  const blockingCriteria =
    matchedIds.length === 0
      ? active.filter(criterion => {
          const remaining = idsMatching(
            index,
            [...predicates.entries()]
              .filter(([kind]) => kind !== criterion)
              .map(([, predicate]) => predicate),
          );
          if (criterion === "commonPaths") return remaining.length > 0;
          return drawnCases(index, remaining, scope.commonPaths, limit).ids.size > 0;
        })
      : [];

  return {
    matchedIds,
    includedIds,
    blockingCriteria,
    simplified: drawn.simplified,
    commonPathsExample: drawn.example,
  };
};

// ---------------------------------------------------------------------------
// Construction and comparison

export const isDefaultInstanceScope = (scope: InstanceScope): boolean =>
  activeScopeCriteria(scope).length === 0 &&
  scope.excludedIds.length === 0 &&
  !scope.apps?.length &&
  scope.minDuration === undefined &&
  scope.maxDuration === undefined;

const sameIdSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index]);

export const sameInstanceScope = (a: InstanceScope, b: InstanceScope): boolean =>
  !!a.noUsers === !!b.noUsers &&
  sameIdSet(a.apps ?? [], b.apps ?? []) &&
  a.minDuration === b.minDuration &&
  a.maxDuration === b.maxDuration &&
  a.minSteps === b.minSteps &&
  a.maxSteps === b.maxSteps &&
  a.commonPaths === b.commonPaths &&
  a.from === b.from &&
  a.to === b.to &&
  a.query?.text === b.query?.text &&
  sameIdSet(a.userIds, b.userIds) &&
  sameIdSet(a.nodeIds, b.nodeIds) &&
  sameIdSet(a.excludedIds, b.excludedIds) &&
  sameIdSet(a.query?.instanceIds ?? [], b.query?.instanceIds ?? []);

/** Drop one criterion, leaving the rest (and the manual unticks) alone. */
export const withoutScopeCriterion = (
  scope: InstanceScope,
  criterion: InstanceScopeCriterion,
): InstanceScope => {
  switch (criterion) {
    case "user":
      return { ...scope, userIds: [], noUsers: false };
    case "node":
      return { ...scope, nodeIds: [] };
    case "date":
      return { ...scope, from: null, to: null };
    case "query":
      return { ...scope, query: null };
    case "apps":
      return { ...scope, apps: [] };
    case "duration":
      return { ...scope, minDuration: undefined, maxDuration: undefined };
    case "steps":
      return { ...scope, minSteps: undefined, maxSteps: undefined };
    case "commonPaths":
      return { ...scope, commonPaths: false };
  }
};

/**
 * A bare id list expressed as a scope: no filters, everything else unticked, and every one of the
 * ids drawn. Deep links and the hash carry ids rather than criteria, so this is how a restored
 * selection re-enters the model without pretending to a structure it never had.
 *
 * Ids naming every case pick nothing out, so they leave the drawing to the map's size like the
 * unfiltered scope they are.
 */
export const instanceScopeFromIds = (index: InstanceScopeIndex, ids: string[]): InstanceScope => {
  const wanted = new Set(ids);
  const excludedIds = index.order.filter(id => !wanted.has(id));
  return {
    ...EMPTY_INSTANCE_SCOPE,
    ...(excludedIds.length > 0 && { commonPaths: false }),
    excludedIds,
  };
};

/**
 * Drop unticks for instances the filters no longer match. Without this a box unticked under one
 * set of filters keeps narrowing the scope long after the instance stopped being visible, so the
 * count reads lower than the list can explain. Exclusions only ever apply to what is on screen.
 */
export const pruneScopeExclusions = (
  index: InstanceScopeIndex,
  scope: InstanceScope,
): InstanceScope => {
  if (scope.excludedIds.length === 0) return scope;
  const visible = new Set(matchInstanceScope(index, { ...scope, excludedIds: [] }).matchedIds);
  const kept = scope.excludedIds.filter(id => visible.has(id));
  return kept.length === scope.excludedIds.length ? scope : { ...scope, excludedIds: kept };
};

/** Manual tick/untick: recorded as an exclusion so re-running the filters keeps the intent. */
export const toggleScopeInstance = (
  scope: InstanceScope,
  instanceId: string,
  included: boolean,
): InstanceScope => {
  const excluded = new Set(scope.excludedIds);
  if (included) excluded.delete(instanceId);
  else excluded.add(instanceId);
  return { ...scope, excludedIds: [...excluded] };
};

// ---------------------------------------------------------------------------
// Options and presets

type ScopeUserOption = {
  userId: string;
  userName: string | null;
  instanceCount: number;
};

/** Distinct recording users, most instances first, for the user chip's menu. */
export const scopeUserOptions = (instances: ProcessInstance[]): ScopeUserOption[] => {
  const byId = new Map<string, ScopeUserOption>();
  for (const instance of instances) {
    const { userId, userName } = instance.source;
    const existing = byId.get(userId);
    if (existing) {
      existing.instanceCount += 1;
      existing.userName ??= userName?.trim() || null;
      continue;
    }
    byId.set(userId, { userId, userName: userName?.trim() || null, instanceCount: 1 });
  }
  return [...byId.values()].sort(
    (a, b) =>
      b.instanceCount - a.instanceCount ||
      (a.userName ?? "").localeCompare(b.userName ?? "", undefined, { sensitivity: "base" }),
  );
};

type ScopeNodeOption = {
  id: string;
  /** The step's name, qualified by its app where it has one. */
  label: string;
  instanceCount: number;
};

/** Steps at least one instance visits, in process order, for the step chip's menu. */
export const scopeNodeOptions = (process: Process): ScopeNodeOption[] =>
  process.nodes.flatMap(node => {
    const instanceCount = process.instances.filter(instance =>
      instance.steps.some(step => step.nodeId === node.id),
    ).length;
    if (instanceCount === 0) return [];
    return [
      {
        id: node.id,
        label: node.app ? `${node.name} · ${node.app}` : node.name,
        instanceCount,
      },
    ];
  });

/** The day bounds covered by the process, used to bracket the date inputs. */
export const scopeDayBounds = (
  index: InstanceScopeIndex,
): { first: string; last: string } | null => {
  const days = [...index.dayById.values()].sort();
  if (days.length === 0) return null;
  return { first: days[0], last: days[days.length - 1] };
};

/** `from` for a "last N days" preset, counting today as day one. */
export const lastDaysFrom = (days: number, today = new Date()): string => {
  const from = new Date(today);
  from.setDate(from.getDate() - (days - 1));
  return dayKeyForDate(from);
};
